import { useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";

export default function TrendsChart(props) {
  const {
    data = [],
    barDataKey = "barValue",
    lineDataKey = "lineValue",
    barLabel = "Games",
    lineLabel = "Play rate",
    barColor = "#a855f7",
    lineColor = "#fbbf24",
    barAxisLabel = "",
    lineAxisLabel = "",
    lineValueSuffix = "%",
    height = 400,
    enableGridX = true,
    enableGridY = true
  } = props;

  const theme = useTheme();

  const chartTheme = useMemo(
    () => ({
      background: "transparent",
      text: {
        fill: theme.palette.text.primary,
        fontSize: 11
      },
      axis: {
        domain: {
          line: {
            stroke: theme.palette.divider,
            strokeWidth: 1
          }
        },
        ticks: {
          line: {
            stroke: theme.palette.divider,
            strokeWidth: 1
          },
          text: {
            fill: theme.palette.text.secondary,
            fontSize: 11
          }
        },
        legend: {
          text: {
            fill: theme.palette.text.primary,
            fontSize: 12,
            fontWeight: 500
          }
        }
      },
      grid: {
        line: {
          stroke: theme.palette.mode === "dark" ? "#374151" : "#e5e7eb",
          strokeWidth: 1
        }
      },
      legends: {
        text: {
          fill: theme.palette.text.primary,
          fontSize: 12
        }
      },
      tooltip: {
        container: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontSize: 12,
          borderRadius: "4px",
          boxShadow: theme.shadows[3],
          padding: "9px 12px"
        }
      }
    }),
    [theme]
  );

  const processedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      [barDataKey]: item[barDataKey] || 0
    }));
  }, [data, barDataKey]);

  const maxBarValue = useMemo(() => {
    return Math.max(...processedData.map((d) => d[barDataKey] || 0));
  }, [processedData, barDataKey]);

  const maxLineValue = useMemo(() => {
    return Math.max(...processedData.map((d) => d[lineDataKey] || 0));
  }, [processedData, lineDataKey]);

  const CustomLayer = ({ bars, xScale, yScale }) => {
    const lineData = processedData.map((item, index) => {
      const xPos = xScale(item.id) + xScale.bandwidth() / 2;
      const yPos = yScale(
        (item[lineDataKey] / maxLineValue) * maxBarValue || 0
      );
      return { x: xPos, y: yPos, value: item[lineDataKey] };
    });

    return (
      <g>
        <path
          d={lineData
            .map((point, i) => `${i === 0 ? "M" : "L"} ${point.x},${point.y}`)
            .join(" ")}
          fill="none"
          stroke={lineColor}
          strokeWidth={3}
        />
        {lineData.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r={4}
            fill={lineColor}
            stroke={lineColor}
            strokeWidth={2}
          />
        ))}
      </g>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <span style={{ color: theme.palette.text.secondary }}>
          No data available
        </span>
      </Box>
    );
  }

  return (
    <Box sx={{ height }}>
      <ResponsiveBar
        data={processedData}
        keys={[barDataKey]}
        indexBy="id"
        margin={{ top: 50, right: 80, bottom: 50, left: 60 }}
        padding={0.3}
        colors={[barColor]}
        borderColor={{
          from: "color",
          modifiers: [["darker", 0.2]]
        }}
        axisTop={null}
        axisRight={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: lineAxisLabel,
          legendPosition: "middle",
          legendOffset: 60,
          format: (value) => {
            const lineVal = (value / maxBarValue) * maxLineValue;
            return `${Math.round(lineVal)}${lineValueSuffix}`;
          }
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "",
          legendPosition: "middle",
          legendOffset: 32
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: barAxisLabel,
          legendPosition: "middle",
          legendOffset: -45
        }}
        enableGridX={enableGridX}
        enableGridY={enableGridY}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{
          from: "color",
          modifiers: [["darker", 2]]
        }}
        legends={[
          {
            dataFrom: "keys",
            data: [
              {
                id: barDataKey,
                label: barLabel,
                color: barColor
              },
              {
                id: lineDataKey,
                label: lineLabel,
                color: lineColor
              }
            ],
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateX: 0,
            translateY: 50,
            itemsSpacing: 20,
            itemWidth: 100,
            itemHeight: 20,
            itemDirection: "left-to-right",
            itemOpacity: 1,
            symbolSize: 12,
            symbolShape: "square"
          }
        ]}
        layers={["grid", "axes", "bars", CustomLayer, "markers", "legends"]}
        theme={chartTheme}
        animate={true}
        motionConfig="gentle"
      />
    </Box>
  );
}
